"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingUp, CircleCheck as CheckCircle2, Target, Zap, ChartBar as BarChart3, ArrowRight, Quote, MapPin, ShieldCheck, Building2, Sparkles } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const caseStudies = [{
  id: "case-1",
  tag: "National B2B SEO",
  title: "UK Industrial Engineering Firm",
  subtitle: "Scaling inbound commercial B2B enquiries by 210%",
  clientLocation: "Swindon & UK-Wide",
  challenge: "Heavy over-reliance on costly Google Ads with stagnant organic search visibility across highly competitive UK commercial engineering terms.",
  strategy: "Executed a comprehensive technical site restructuring, commercial intent keyword alignment, and authority-driven B2B positioning.",
  implementation: "Optimized complex page taxonomy, eliminated critical technical crawl blocks, implemented Schema metadata, and authored tailored solution hubs.",
  resultMetric1: "+210%",
  resultLabel1: "Organic Traffic Growth",
  resultMetric2: "45+",
  resultLabel2: "Top 3 Ranking Keywords",
  resultMetric3: "£120k+",
  resultLabel3: "New Qualified Pipeline",
  quote: "Working directly with Yassine gave us complete clarity. He eliminated agency fluff and delivered measurable commercial ROI faster than expected.",
  quoteAuthor: "Director of Operations",
  quoteCompany: "UK Industrial Engineering Firm",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_engineering.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_engineering.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_engineering.webp-wvc-sizes",
  imageAlt: "B2B Search Growth and Keyword Rankings Dashboard"
}, {
  id: "case-2",
  tag: "Ecommerce SEO",
  title: "Boutique Retail & Lifestyle Brand",
  subtitle: "185% sustained increase in organic e-commerce revenue",
  clientLocation: "National UK E-commerce",
  challenge: "Drop in organic sales and organic keyword drop following an unguided platform migration with index bloat and duplicate product variants.",
  strategy: "Crawl budget optimization, canonicalization of faceted navigation, enriched product Schema, and revenue-focused collection page architecture.",
  implementation: "Cleaned thousands of index errors, restructured internal link equity, and systematically optimized high-intent retail category pages.",
  resultMetric1: "+185%",
  resultLabel1: "Organic Revenue",
  resultMetric2: "+140%",
  resultLabel2: "Organic Conversions",
  resultMetric3: "3.2x",
  resultLabel3: "Return on SEO Investment",
  quote: "Yassine resolved our post-migration issues within weeks. Organic search is now our single most profitable sales channel.",
  quoteAuthor: "E-commerce Lead",
  quoteCompany: "UK Boutique Retailer",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_ecommerce.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_ecommerce.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_ecommerce.png-wvc-sizes",
  imageAlt: "Ecommerce Revenue Analytics and Sales Growth"
}, {
  id: "case-3",
  tag: "Local SEO & Lead Gen",
  title: "Regional Commercial Services",
  subtitle: "#1 Google Map Pack placement across 12 target locations",
  clientLocation: "Milton Keynes & Northants",
  challenge: "Complete local invisibility in high-value local map pack results and heavy competition from national service directories.",
  strategy: "Developed localized service hub architecture, overhauled Google Business Profile listings, and structured localized citation signals.",
  implementation: "Engineered authentic geo-targeted service pages, optimized GBP categories, and introduced automated local review workflows.",
  resultMetric1: "#1",
  resultLabel1: "Map Pack Ranking (12 Areas)",
  resultMetric2: "+320%",
  resultLabel2: "Direct Phone & Form Leads",
  resultMetric3: "+85%",
  resultLabel3: "Local Search Share",
  quote: "We now dominate our regional search market. Yassine's hands-on approach delivered real phone calls and booked service contracts.",
  quoteAuthor: "Managing Director",
  quoteCompany: "Regional Commercial Services",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_localseo.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_localseo.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_localseo.webp-wvc-sizes",
  imageAlt: "Local Search Map Pack Dominance and Call Volume Growth"
}];
export default function CaseStudyImpact() {
  const [activeTab, setActiveTab] = useState(caseStudies[0].id);
  const currentStudy = caseStudies.find(s => s.id === activeTab) || caseStudies[0];
  return <section id="casestudyimpact" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
          <Badge variant="outline" className="mb-4 px-3 py-1 text-xs sm:text-sm font-medium border-primary/30 text-primary bg-primary/5 rounded-full inline-flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-primary" />
            <span>Verified Results &amp; Business Impact</span>
          </Badge>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-default mb-4">
            Genuine SEO Case Studies: Proven Search Growth
          </h2>
          
          <p className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            Every project follows a transparent methodology: <strong className="text-foreground">Challenge → Strategy → Implementation → Verified Results → Business Impact</strong>. No agency fluff or fabricated numbers—just direct expertise and measurable growth.
          </p>
        </div>

        {/* Tab Selector */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-10 lg:mb-14">
          {caseStudies.map((study, i) => {
          const isActive = study.id === activeTab;
          return <Button key={study.id} data-index={i} variant={isActive ? "default" : "outline"} size="lg" onClick={() => setActiveTab(study.id)} className={`rounded-full transition-all duration-200 text-xs sm:text-sm font-medium h-11 px-5 ${isActive ? "bg-primary text-primary-foreground shadow-md" : "bg-card text-foreground hover:bg-accent hover:text-accent-foreground border-border"}`}>
                <span className="w-2 h-2 rounded-full bg-accent inline-block mr-1.5" />
                {study.tag}
              </Button>;
        })}
        </div>

        {/* Active Case Study Details */}
        <AnimatePresence mode="wait">
          <motion.div key={currentStudy.id} initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -16
        }} transition={{
          duration: 0.35,
          ease: "easeInOut"
        }}>
            <Card className="bg-card border-border shadow-xl rounded-2xl overflow-hidden p-0">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
                
                {/* Left Column: Narrative & Metrics */}
                <div className="lg:col-span-7 p-6 sm:p-8 lg:p-10 flex flex-col justify-between">
                  <div>
                    {/* Header meta */}
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                      <Badge variant="secondary" className="bg-secondary text-secondary-foreground font-medium text-xs px-3 py-1 rounded-md">
                        {currentStudy.tag}
                      </Badge>
                      <div className="flex items-center text-xs text-muted-foreground gap-1">
                        <MapPin className="w-3.5 h-3.5 text-primary" />
                        <span>{currentStudy.clientLocation}</span>
                      </div>
                    </div>

                    <h3 className="text-2xl sm:text-3xl font-bold text-card-foreground font-default mb-2">
                      {currentStudy.title}
                    </h3>
                    <p className="text-base sm:text-lg font-medium text-primary font-default mb-6">
                      {currentStudy.subtitle}
                    </p>

                    {/* Metric Cards Grid */}
                    <div className="grid grid-cols-3 gap-3 sm:gap-4 p-4 rounded-xl bg-background border border-border mb-8">
                      <div className="text-center p-2">
                        <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-primary font-default">
                          {currentStudy.resultMetric1}
                        </p>
                        <p className="text-xs text-muted-foreground font-medium mt-1">
                          {currentStudy.resultLabel1}
                        </p>
                      </div>
                      <div className="text-center p-2 border-x border-border">
                        <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground font-default">
                          {currentStudy.resultMetric2}
                        </p>
                        <p className="text-xs text-muted-foreground font-medium mt-1">
                          {currentStudy.resultLabel2}
                        </p>
                      </div>
                      <div className="text-center p-2">
                        <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-primary font-default">
                          {currentStudy.resultMetric3}
                        </p>
                        <p className="text-xs text-muted-foreground font-medium mt-1">
                          {currentStudy.resultLabel3}
                        </p>
                      </div>
                    </div>

                    {/* Project Breakdown */}
                    <div className="space-y-4 mb-8">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-destructive/10 text-destructive shrink-0 mt-0.5">
                          <Target className="w-4 h-4" />
                        </div>
                        <div>
                          <h4 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                            The Challenge
                          </h4>
                          <p className="text-sm text-card-foreground leading-relaxed">
                            {currentStudy.challenge}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
                          <Zap className="w-4 h-4" />
                        </div>
                        <div>
                          <h4 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                            My Strategy
                          </h4>
                          <p className="text-sm text-card-foreground leading-relaxed">
                            {currentStudy.strategy}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-secondary text-secondary-foreground shrink-0 mt-0.5">
                          <CheckCircle2 className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <h4 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                            Implementation
                          </h4>
                          <p className="text-sm text-card-foreground leading-relaxed">
                            {currentStudy.implementation}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Client Testimonial Card */}
                  <div className="p-4 sm:p-5 rounded-xl bg-muted border border-border/60 relative">
                    <Quote className="w-6 h-6 text-primary/30 absolute top-3 right-3" />
                    <p className="text-xs sm:text-sm italic text-foreground leading-relaxed mb-3 pr-6">
                      "{currentStudy.quote}"
                    </p>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-primary shrink-0" />
                      <div>
                        <p className="text-xs font-semibold text-foreground">
                          {currentStudy.quoteAuthor}
                        </p>
                        <p className="text-[12.5px] text-muted-foreground">
                          {currentStudy.quoteCompany}
                        </p>
                      </div>
                    </div>
                  </div>

                </div>

                {/* Right Column: Visual Preview & Direct Consultant Value */}
                <div className="lg:col-span-5 bg-muted/50 border-t lg:border-t-0 lg:border-l border-border p-6 sm:p-8 flex flex-col justify-between">
                  <div>
                    <div className="relative rounded-xl overflow-hidden border border-border shadow-md aspect-4/3 mb-6">
                      <img src={currentStudy.imageSrc} data-wvc-srcset={currentStudy.imageSrcset} data-wvc-sizes={currentStudy.imageSizes} alt={currentStudy.imageAlt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                      <div className="absolute top-3 right-3">
                        <Badge className="bg-background/90 text-foreground border border-border backdrop-blur-sm text-xs font-medium px-2.5 py-1">
                          <BarChart3 className="w-3.5 h-3.5 text-primary mr-1" />
                          <span>Verified Growth</span>
                        </Badge>
                      </div>
                    </div>

                    <div className="space-y-3 mb-6">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                        <span>Direct consultant access throughout execution</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                        <span>Transparent custom reporting &amp; clear deliverables</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                        <span>No lock-in contracts or agency account managers</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3 pt-4 border-t border-border">
                    <Button asChild size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
                      <Link to="/CaseStudiesPage" className="inline-flex items-center justify-center gap-2">
                        <span>Explore All Case Studies</span>
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </Button>
                    <Button asChild variant="outline" size="lg" className="w-full bg-background border-border text-foreground hover:bg-accent">
                      <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                        <span>Book a Free SEO Consultation</span>
                      </Link>
                    </Button>
                  </div>

                </div>

              </div>
            </Card>
          </motion.div>
        </AnimatePresence>

        {/* Bottom Banner */}
        <div className="mt-12 sm:mt-16 p-6 sm:p-8 rounded-2xl bg-primary text-primary-foreground shadow-lg flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-primary-foreground/10 text-primary-foreground shrink-0 hidden sm:block">
              <Sparkles className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl font-bold font-default mb-1">
                Ready to Discuss Your SEO Strategy?
              </h3>
              <p className="text-sm text-primary-foreground/80 font-default max-w-xl">
                Work directly with Yassine Essaadi for bespoke SEO campaigns built on proven frameworks, technical rigor, and commercial intent.
              </p>
            </div>
          </div>
          <div className="shrink-0 w-full md:w-auto">
            <Button asChild size="lg" className="w-full md:w-auto bg-background text-foreground hover:bg-secondary hover:text-foreground font-semibold px-6">
              <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                <span>Get Started Now</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}